import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import { useEffect, useState } from 'react'
import { api } from '../../api'
import { Icon } from '../../components/Icon'

/**
 * A snippet reference in the editor: the shared block, shown where it will
 * appear, edited only in its own file.
 *
 * An ATOM on purpose — the body is not in this document, so nothing here is
 * editable in place. Making it look editable would invite edits that vanish
 * on save; instead the header says where the words live, and the preview
 * below is rendered by the same server endpoint the page preview uses, so
 * what the writer sees here is what the reader gets.
 *
 * A reference with no name yet (fresh from the insert menu) is a picker: the
 * available snippets, fetched when needed, chosen with one click.
 */
export function SnippetView({ node, updateAttributes, editor }: NodeViewProps) {
  const name = String(node.attrs['name'] ?? '')
  const [html, setHtml] = useState<string | null>(null)
  const [choices, setChoices] = useState<{ name: string }[] | null>(null)

  useEffect(() => {
    let gone = false
    if (!name) {
      void api
        .snippets()
        .then((r) => !gone && setChoices(r.snippets))
        .catch(() => !gone && setChoices([]))
      return () => {
        gone = true
      }
    }
    void api
      .preview(`::snippet{name="${name}"}`)
      .then((r) => !gone && setHtml(r.html))
      .catch(() => !gone && setHtml(null))
    return () => {
      gone = true
    }
  }, [name])

  return (
    <NodeViewWrapper className="be-snippet" data-name={name || undefined}>
      <div className="be-snippet__head" contentEditable={false}>
        <Icon name="blocks" size={12} />
        {name ? (
          <>
            <span className="be-snippet__name mono">{name}</span>
            <span className="be-snippet__note">
              shared block — edits happen in snippets/{name}.md
            </span>
          </>
        ) : (
          <span className="be-snippet__note">Choose a snippet to show here</span>
        )}
      </div>
      {!name && choices && (
        <div className="be-snippet__pick" contentEditable={false}>
          {choices.length === 0 ? (
            <span className="be-snippet__note">
              No snippets yet — create one under Variables, then come back.
            </span>
          ) : (
            choices.map((c) => (
              <button
                key={c.name}
                className="btn btn--sm btn--outline"
                onClick={() => updateAttributes({ name: c.name })}
              >
                {c.name}
              </button>
            ))
          )}
        </div>
      )}
      {name && html !== null && (
        <div
          className="be-snippet__body preview"
          contentEditable={false}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
      {name && html === null && !editor.isEditable && null}
    </NodeViewWrapper>
  )
}
